/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable react/no-array-index-key */
import React, {
    useState,
    Fragment,
} from 'react'

import {
    ToolbarButton,
    ColumnsPanelTrigger,
    FilterPanelTrigger,
} from '@mui/x-data-grid-pro'
import Badge from '@mui/material/Badge'
import Tooltip from '@mui/material/Tooltip'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import ViewColumnIcon from '@mui/icons-material/ViewColumn'
import MoreVertIcon from '@mui/icons-material/MoreVert'

import useClearSorting from 'app/PoC/DataGridDemoPages/components/SkyBaseDataGrid/hooks/useClearSorting'
import ClearSortingIcon
    from 'app/PoC/DataGridDemoPages/components/SkyBaseDataGrid/components/SkyBaseDataGridToolbar/ClearSortingIcon'
import useActiveFiltersCount from 'app/PoC/DataGridDemoPages/components/SkyBaseDataGrid/hooks/useActiveFiltersCount'
import FilterIcon
    from 'app/PoC/DataGridDemoPages/components/SkyBaseDataGrid/components/SkyBaseDataGridToolbar/FilterIcon'

import useStyles from '../SkyNetDataGridToolbar.style'
import {
    SetPanelTargetElFn, ToolbarSharedProps,
} from '../SkyNetDataGridToolbar'
import useAllowedToolbarActions from '../hooks/useAllowedToolbarActions'

type Props = ToolbarSharedProps & {
    setPanelTargetEl: SetPanelTargetElFn
}

const ReducedViewToolbarActions = ({
    toolbarControls,
    showFilterButton,
    setPanelTargetEl,
    toolbarActions,
    toolbarActionsProps,
}: Readonly<Props>) => {
    const {
        classes,
    } = useStyles()
    const [
        anchorEl,
        setAnchorEl,
    ] = useState<null | HTMLElement>(null)
    const handleClearSorting = useClearSorting()
    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget)
    }
    const handleClose = () => {
        setAnchorEl(null)
    }

    const actions = useAllowedToolbarActions({
        toolbarActions,
        toolbarActionsProps: {
            ...toolbarActionsProps,
            isMenuItem: true,
        },
    })
    const activeFiltersCount = useActiveFiltersCount(showFilterButton)

    return (
        <>
            <Tooltip title="Actions">
                <ToolbarButton
                    ref={setPanelTargetEl}
                    onClick={handleClick}
                    data-testid="actions-menu-btn"
                >
                    <Badge
                        badgeContent={activeFiltersCount}
                        color="primary"
                        variant="dot"
                    >
                        <MoreVertIcon fontSize="small" />
                    </Badge>
                </ToolbarButton>
            </Tooltip>

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                className={classes.dropdown}
                data-testid="actions-menu"
            >
                {actions.map((action, index) => {
                    return (
                        <Fragment key={`toolbar-action-${index}`}>
                            {action}
                        </Fragment>
                    )
                })}

                {toolbarControls?.map((control, index) => {
                    return <MenuItem key={`toolbar-control-${index}`}>{control}</MenuItem>
                })}
                {showFilterButton && (
                    <FilterPanelTrigger
                        render={({
                            onClick,
                        }) => {
                            return (
                                <MenuItem
                                    onClick={(e) => {
                                        onClick?.(e)
                                        handleClose()
                                    }}
                                    data-testid="filter-menu-item"
                                >
                                    <ListItemIcon>
                                        <Badge
                                            badgeContent={activeFiltersCount}
                                            color="primary"
                                            variant="dot"
                                        >
                                            <FilterIcon
                                                className={classes.filter}
                                            />
                                        </Badge>
                                    </ListItemIcon>
                                    <ListItemText primary="Filters" />
                                </MenuItem>
                            )
                        }}
                    />
                )}
                <MenuItem
                    onClick={() => {
                        handleClearSorting()
                        handleClose()
                    }}
                    data-testid="clear-sorting-menu-item"
                >
                    <ListItemIcon>
                        <ClearSortingIcon />
                    </ListItemIcon>
                    <ListItemText primary="Clear Sorting" />
                </MenuItem>
                <ColumnsPanelTrigger
                    render={({
                        onClick,
                    }) => {
                        return (
                            <MenuItem
                                onClick={(e) => {
                                    onClick?.(e)
                                    handleClose()
                                }}
                                data-testid="columns-menu-item"
                            >
                                <ListItemIcon>
                                    <ViewColumnIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText primary="Columns" />
                            </MenuItem>
                        )
                    }}
                />
            </Menu>
        </>
    )
}

export default ReducedViewToolbarActions
